import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) { setLoading(false); return; }
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser({ ...data, token });
    } catch (err) {
      // If 403 = deactivated, force logout
      if (err.response?.status === 403 || err.response?.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Poll every 15s to detect deactivation
  useEffect(() => {
    if (user && user.role === "agent") {
      pollRef.current = setInterval(async () => {
        try {
          const token = localStorage.getItem("token");
          if (!token) return;
          await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          });
        } catch (err) {
          if (err.response?.status === 403) {
            localStorage.removeItem("token");
            setUser(null);
            window.location.href = "/login";
          }
        }
      }, 15000);
      return () => clearInterval(pollRef.current);
    }
  }, [user]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    localStorage.setItem("token", data.token);
    localStorage.setItem("login_time", new Date().toISOString());
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
    } catch {}
    localStorage.removeItem("token");
    localStorage.removeItem("login_time");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function apiCall(method, url, data = null) {
  const config = { method, url: `${API}${url}`, headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true };
  if (data) config.data = data;
  return axios(config);
}
